<script setup lang="ts">
import type { MapClickLocation, RegionMapProps } from '.';

import { Container } from '@/components/Container';
import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { dehyphenateString } from '~~/utils';

const props = defineProps<RegionMapProps>();

const region = computed<string>(() => dehyphenateString(props.name));

const { trackCtaViewMoreClicked, trackMapListingClicked } = useEvents();
const route = useRoute();

const handleCtaClick = (ctaText: string, destination_url: string) => {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: String(route.fullPath),
		[MixpanelProps.SECTION_NAME]: 'neighborhood presentation',
		cta_text: ctaText,
		destination_url: destination_url
	});
};

const handleMapClick = (
	click_location: MapClickLocation,
	destination_url: string
) => {
	trackMapListingClicked({
		[MixpanelProps.CURRENT_PAGE]: String(route.fullPath),
		[MixpanelProps.CLICK_LOCATION]: click_location,
		[MixpanelProps.CTA_TEXT]: 'Ver no mapa',
		[MixpanelProps.DESTINATION_URL]: destination_url,
		[MixpanelProps.FILTER_NEIGHBORHOOD]: region.value,
		[MixpanelProps.PROPERTY_COUNT]: 0
	});
};

const currentRegion = computed(() => {
	return route.fullPath.split('/').pop();
});
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
	<Container class="bg-white pt-0! pb-8 2xl:pb-15 2xl:pt-4">
		<div class="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:gap-12">
			<div v-if="isLoading" class="space-y-10">
				<div class="space-y-5">
					<Skeleton is-active class="h-10 w-full" variant="white" />
					<Skeleton is-active class="h-10 w-1/2" variant="white" />
				</div>

				<div
					v-for="group in 2"
					:key="`skeleton-group-${group}`"
					class="space-y-4"
				>
					<Skeleton
						v-for="line in 6"
						:key="`skeleton-line-${line}-${group}`"
						is-active
						class="h-4 w-full last:w-1/2"
						variant="white"
					/>
				</div>

				<Skeleton is-active class="h-12 w-2/3" variant="white" />
			</div>
			<div v-else class="col-span-1 flex flex-col gap-6">
				<h1
					class="font-matter-sq text-[1.75rem] font-normal tracking-[-0.035rem] text-primary 2xl:text-[2.75rem] 2xl:tracking-[-0.055rem]"
				>
					{{ title }}
				</h1>
				<p
					class="text-base font-normal leading-[175%] text-primary"
					v-html="content"
				/>

				<div class="flex w-full flex-col gap-3 lg:flex-row">
					<NuxtLink
						v-for="link in links"
						:key="`region-map-cta:${link.url}`"
						v-slot="{ href, navigate }"
						custom
						:to="{
							path: link.url
						}"
						@click="handleCtaClick(link.label, link.url)"
					>
						<Button
							:href="href"
							class="w-full cursor-pointer md:w-min 2xl:px-26"
							variant="primary"
							size="md"
							as="a"
							@click="navigate"
						>
							{{ link.label }}
						</Button>
					</NuxtLink>
				</div>
			</div>
			<div
				class="col-span-1 flex flex-col gap-3 lg:flex-col-reverse lg:justify-end"
			>
				<Skeleton
					class="h-[18px] w-[180px]"
					variant="white"
					:is-active="isLoading"
				>
					<span class="text-[1.125rem] font-normal leading-normal text-primary">
						Mapa {{ name }}
					</span>
				</Skeleton>
				<div class="relative w-full">
					<div
						class="h-80 w-full bg-map-placeholder-image bg-cover bg-no-repeat md:h-[500px] lg:h-[550px] lg:w-full"
					>
						<NuxtImg
							:src="image"
							preload
							format="webp"
							width="50vw"
							:alt="`Bairro ${name}`"
							class="size-full"
						/>
					</div>
					<NuxtLink
						v-slot="{ href, navigate }"
						custom
						:to="
							PropertyFiltersPresenter.getPathFromFilters(
								{
									states: ['sp'],
									cities: ['São Paulo'],
									regions: [String(currentRegion)],
									macroregions: ['São Paulo']
								},
								{},
								{ page: 'imoveis' }
							)
						"
					>
						<Button
							class="absolute bottom-5 right-5 h-11 w-min cursor-pointer gap-2 px-4 py-1 shadow"
							variant="secondary"
							size="sm"
							as="a"
							:href="href"
							skeleton-variant="whiteHighlight"
							:is-skeleton-loading="isLoading"
							skeleton-classes="w-[180px] h-11 absolute bottom-5 right-5"
							@click="
								() => {
									handleMapClick('cta', href);
									navigate;
								}
							"
						>
							<IconMaterialSymbolsMapOutline class="size-5 text-primary" />
							<span class="text-sm font-semibold"> Ver no mapa </span>
						</Button>
					</NuxtLink>
				</div>
			</div>
		</div>
	</Container>
</template>
